import React, { useCallback, useEffect } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { Map } from 'immutable';
import moment from 'moment';

import { setSnackbar } from 'flow/actions/app-actions';
import {
    fetchSharedVoicemail,
    removeAllSharedVoiceMessages,
} from 'flow/actions/pbx-actions';
import { getVoiceMessageActions } from 'flow/actions/voicemail-actions';
import CallButton from 'flow/components/CallButton';
import PbxMenuIcon from 'flow/components/pbx/PbxMenuIcon';
import PbxSharedVoicemailItem from 'flow/components/pbx/shared-voicemail/PbxSharedVoicemailItem';
import VoiceMessageBanner from 'flow/components/pbx/shared-voicemail/VoiceMessageBanner';
import Page from 'flow/components/Setting/Page';
import SettingSection from 'flow/components/Setting/SettingSection';
import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import NumberWrapper from 'flow/components/ui-components/NumberWrapper';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { VoicemailKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { PbxSharedVoiceMessage } from 'flow/types/VoiceMessage';
import { getDateDescription, groupListBy } from 'flow/utils';

const PbxSharedVoicemail = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { openModal } = useModal();
    const { isSinglePaneMode } = useSinglePaneMode();

    const { entityKey: voicemailKey } = useParams<{
        entityKey: VoicemailKey;
    }>();

    const voicemail = useAppSelector((state) =>
        state.pbx.getIn(['voicemails', voicemailKey], Map()),
    );

    const voicemailPhoneNumber = voicemail.getIn(['number', 'number']);
    const voiceMessages = voicemail?.get('voiceMessages');

    useEffect(() => {
        voiceMessages?.forEach((voiceMessage) => {
            dispatch(
                getVoiceMessageActions(
                    voicemailKey,
                    voiceMessage.get('uniqueId'),
                ),
            );
        });
    }, [dispatch, voiceMessages, voicemailKey]);

    useEffect(() => {
        const intervalID = setInterval(() => {
            dispatch(fetchSharedVoicemail(voicemailKey, 'contact'));
        }, 30000);

        return () => clearInterval(intervalID);
    }, [dispatch, voicemailKey]);

    const sortedDateKeys: string[] = voiceMessages
        ? groupListBy(
              voiceMessages,
              (message: TypedMap<PbxSharedVoiceMessage>) =>
                  moment(message.get('receivedDate')).format('YYYY-MM-DD'),
          )
              .keySeq()
              .sort()
              .reverse()
        : null;

    const handleRemoveAllVoicemessages = useCallback(() => {
        try {
            dispatch(removeAllSharedVoiceMessages(voicemailKey));
        } catch (_err) {
            dispatch(
                setSnackbar({
                    i18nextMessage: 'Settings.Update.Snackbar.Fail',
                }),
            );
        }
    }, [voicemailKey, dispatch]);

    const voicemailItems = (date: string) => {
        const filteredVoiceItems = voiceMessages
            .filter(
                (voiceMessage) =>
                    moment(voiceMessage.get('receivedDate')).format(
                        'YYYY-MM-DD',
                    ) === date,
            )
            .valueSeq();

        return (
            <div>
                {filteredVoiceItems.map((voicemessage) => (
                    <PbxSharedVoicemailItem
                        key={`voicemail-${voicemessage?.get('uniqueId')}`}
                        voiceMessage={voicemessage}
                        voicemailKey={voicemailKey}
                        voiceMessagesActions={voicemail.get(
                            'voiceMessageActions',
                        )}
                    />
                ))}
            </div>
        );
    };

    return (
        <Page
            header={voicemail.get('description')}
            onClickBack={
                isSinglePaneMode ? () => history.push(routes['pbx']) : undefined
            }
            action={
                <IconButton
                    tooltipLabel={i18next.t('settings')}
                    onClick={() =>
                        history.push(
                            generatePath(
                                routes['pbx.voicemails.entityKey.settings'],
                                {
                                    entityKey: voicemailKey,
                                },
                            ),
                        )
                    }
                >
                    <Icon iconKey="setting_menu" />
                </IconButton>
            }
        >
            <div className="flex h-13 items-center justify-between rounded-sm p-2">
                <div className="flex items-center gap-x-3">
                    <PbxMenuIcon iconKey="voicemail" />

                    <NumberWrapper>
                        {voicemail.getIn(['number', 'prettyNationalNumber'])}
                    </NumberWrapper>
                </div>

                {voicemailPhoneNumber && (
                    <div className="flex justify-end">
                        <CallButton phoneNumber={voicemailPhoneNumber} />
                    </div>
                )}
            </div>
            <Divider />

            <VoiceMessageBanner
                total={voicemail.get('voiceMessages', Map()).count()}
            />

            <SettingSection className="text-xl">
                {i18next.t('call.voicemessage')}
            </SettingSection>

            <div className="px-2">
                {sortedDateKeys?.map((date) => (
                    <div key={`voicemail-date-group-${date}`}>
                        <Divider key={`datekey-${date}`}>
                            {getDateDescription(date, i18next)}
                        </Divider>
                        {voicemailItems(date)}
                    </div>
                ))}
                {voiceMessages?.size > 0 && (
                    <div>
                        <Button
                            className="mt-5 w-full"
                            color="danger"
                            onClick={() =>
                                openModal('removeDialog', {
                                    title: i18next.t(
                                        'call.voicemail.remove.title',
                                    ),
                                    description: i18next.t(
                                        'call.voicemail.remove.question',
                                    ),
                                    onRemove: handleRemoveAllVoicemessages,
                                })
                            }
                        >
                            {i18next.t('call.voicemail.delete.title')}
                        </Button>
                    </div>
                )}
            </div>
        </Page>
    );
};

export default PbxSharedVoicemail;
